// =============================================================================
// BucketPicker: LANE W2.1-BUCKETS — the user-sort half of Jason's ruling
// ("pass is user selected and then unsorted to be sorted by user"). A minimal
// affordance (a plain <select>, no long form — hard rule #2) that lets the
// user explicitly set a job's bucket, including 'pass' (the one value the
// auto-scorer itself can never produce, see bucketJob.ts's AutoJobBucket).
//
// Thin: this component renders the picker and calls back with the chosen
// bucket — it does NOT call the API itself (JobCard/Jobs.tsx thread the
// actual PATCH /jobs/:id/bucket call in via useJobsInbox's setJobBucket, kept
// out of this presentation-only component per the no-frontend-bandaids law:
// the WRITE belongs to the data layer, not a UI leaf).
//
// stopPropagation on every interaction: JobCard is normally wrapped in a
// react-router Link (Jobs.tsx) — the exact same "clickable thing inside a
// card-that-is-a-link" problem the "Open source" link already solves in
// JobCard.tsx, solved here the identical way.
// =============================================================================

import { useState } from 'react';
import { BUCKET_PICKER_OPTIONS } from '../lib/jobBuckets';
import type { JobBucket } from '../lib/jobBuckets';
import styles from './BucketPicker.module.css';

export interface BucketPickerProps {
  /** The job's CURRENT bucket — null (never scored / legacy row) renders as
   *  the honest 'unsorted' selection, never a fabricated pick. */
  bucket: JobBucket | null;
  /** Called with the newly-picked bucket. The caller (Jobs.tsx via
   *  useJobsInbox's setJobBucket) owns the actual PATCH call + any
   *  loading/error handling — this component only reports the user's choice
   *  and awaits the returned promise (if any) to know when to re-enable the
   *  control; any resolved value is intentionally unused here. */
  // biome-ignore lint/suspicious/noExplicitAny: caller's resolved value is intentionally unused here
  onChange: (bucket: JobBucket) => Promise<any> | void;
  /** Disables the control while a change is in flight, so a second pick can't
   *  race the first one's PATCH. */
  disabled?: boolean;
}

export function BucketPicker({ bucket, onChange, disabled }: BucketPickerProps) {
  const [pending, setPending] = useState(false);
  const value = bucket ?? 'unsorted';

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as JobBucket;
    if (next === value) return;
    setPending(true);
    try {
      await onChange(next);
    } catch {
      // BOARD-TRUTH FIX: onChange (useJobsInbox's setJobBucket) rethrows on a
      // failed PATCH so ITS OWN caller can read the honest jobId-scoped
      // bucketError it set before rethrowing — this component's job is only
      // to stop that rejection going unhandled here, never to re-surface it
      // itself (the caller, Jobs.tsx, renders the real message from
      // inbox.bucketError so a failure is never silent). The `value` prop
      // above is driven by the job's OWN bucket field from server state,
      // which setJobBucket deliberately did NOT update on failure — so this
      // select's displayed selection correctly snaps back to the truth on
      // the next render, never lingering on the picked-but-unsaved value.
    } finally {
      setPending(false);
    }
  };

  return (
    <select
      className={styles.picker}
      value={value}
      disabled={disabled || pending}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onChange={handleChange}
      aria-label="Set this job's bucket"
      title="Move this job to a different bucket"
    >
      {BUCKET_PICKER_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value} title={opt.note}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}
